import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye, 
  DollarSign,
  Package,
  User,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { format } from 'date-fns';
import { generateOrderXML } from '../../utils/xmlGenerator';
import PageHeader from '../shared/PageHeader';

const AdminOrderApproval = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [savingChanges, setSavingChanges] = useState(false);

  useEffect(() => {
    fetchPendingOrders();
    // Set up real-time subscription for order updates
    const subscription = supabase
      .channel('admin_order_approvals')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: 'internal_status=eq.pending_approval'
        },
        () => {
          fetchPendingOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  const fetchPendingOrders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_id,
          order_number,
          cart_items,
          created_at,
          order_instructions,
          allow_partial_delivery,
          delivery_type,
          freight_carrier,
          freight_send_via,
          freight_account,
          shipping_instructions,
          has_backorders,
          backorder_items,
          accounts:user_id (
            id,
            company_name,
            full_name,
            email
          ),
          delivery_addresses!delivery_address_id (
            address_line1,
            address_line2,
            city,
            state,
            postal_code
          )
        `)
        .eq('internal_status', 'pending_approval')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingOrders(data || []);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      showError(error, 'Failed to fetch pending orders');
    } finally {
      setLoading(false);
    }
  };


  const handleApproveOrder = async (order) => {
    setActionType('approve');
    setViewingOrder(order);
    setIsViewModalOpen(true);
  };

  const handleRejectOrder = async (order) => {
    setActionType('reject');
    setViewingOrder(order);
    setIsViewModalOpen(true);
  };

  const handleDeleteOrder = async (order) => {
    setActionType('delete');
    setViewingOrder(order);
    setIsViewModalOpen(true);
  };

  const confirmDeletion = async () => {
    if (!viewingOrder) return;

    setApproving(prev => ({ ...prev, [viewingOrder.id]: true }));
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Use the database function to delete the order
      const { data: functionResult, error: functionError } = await supabase
        .rpc('delete_order', {
          target_order_id: viewingOrder.id,
          staff_user_id: user.id
        });

      if (functionError) throw functionError;

      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Failed to delete order');
      }

      showNotification(`Order #${functionResult.order_number} deleted successfully`, 'success');
      setIsViewModalOpen(false);
      fetchPendingOrders();

    } catch (error) {
      console.error('Error deleting order:', error);
      showError(error, 'Failed to delete order');
    } finally {
      setApproving(prev => ({ ...prev, [viewingOrder.id]: false }));
    }
  };

  const confirmApproval = async () => {
    if (!viewingOrder) return;

    setApproving(prev => ({ ...prev, [viewingOrder.id]: true }));
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get account data for folder structure and XML generation
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id')
        .eq('id', viewingOrder.accounts.id)
        .single();
      
      if (accountError) throw accountError;
      if (!accountData.deb_id) throw new Error('Missing debtor ID for this account');

      // Generate XML for the order with proper account number
      const xml = generateOrderXML(viewingOrder, accountData.deb_id);
      
      // Create folder structure with deb_id
      const debId = accountData.deb_id || 'unknown';
      const fileName = `${debId}/order_${Date.now()}_approved.xml`;
      
      // Store XML in Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('orders')
        .upload(fileName, xml, {
          contentType: 'application/xml',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL of the uploaded file
      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('orders')
        .getPublicUrl(fileName);

      if (urlError) throw urlError;

      // Use the database function to approve the order (with any updated cart items)
      const { data: functionResult, error: functionError } = await supabase
        .rpc('approve_order', {
          order_id_param: viewingOrder.id,
          staff_user_id: user.id,
          approval_notes: approvalNotes || null,
          updated_cart_items: editedItems.length > 0 ? editedItems : null
        });

      if (functionError) throw functionError;

      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Failed to approve order');
      }

      // Update the order with XML details
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          xml_url: publicUrl,
          xml_generated_at: new Date().toISOString()
        })
        .eq('id', viewingOrder.id);

      if (updateError) throw updateError;

      showNotification(`Order #${viewingOrder.order_number} approved successfully`, 'success');
      setIsViewModalOpen(false);
      setApprovalNotes('');
      fetchPendingOrders();

    } catch (error) {
      console.error('Error approving order:', error);
      showError(error, 'Failed to approve order');
    } finally {
      setApproving(prev => ({ ...prev, [viewingOrder.id]: false }));
    }
  };

  const confirmRejection = async () => {
    if (!viewingOrder || !rejectionReason.trim()) {
      showError(new Error('Rejection reason is required'), 'Please provide a reason for rejection');
      return;
    }

    setApproving(prev => ({ ...prev, [viewingOrder.id]: true }));
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Use the database function to reject the order
      const { data: functionResult, error: functionError } = await supabase
        .rpc('reject_order', {
          order_id_param: viewingOrder.id,
          staff_user_id: user.id,
          rejection_reason_param: rejectionReason
        });

      if (functionError) throw functionError;

      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Failed to reject order');
      }

      showNotification(`Order #${viewingOrder.order_number} rejected`, 'success');
      setIsViewModalOpen(false);
      setRejectionReason('');
      fetchPendingOrders();

    } catch (error) {
      console.error('Error rejecting order:', error);
      showError(error, 'Failed to reject order');
    } finally {
      setApproving(prev => ({ ...prev, [viewingOrder.id]: false }));
    }
  };

  const calculateOrderTotal = (cartItems) => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
    }, 0);
  };

  const handleEditMode = () => {
    setIsEditMode(true);
    setEditedItems([...viewingOrder.cart_items]);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedItems([]);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...editedItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setEditedItems(updatedItems);
  };

  const handleSaveChanges = async () => {
    if (!viewingOrder || !editedItems.length) return;

    // Validate all items
    for (let i = 0; i < editedItems.length; i++) {
      const item = editedItems[i];
      if (!item.quantity || parseInt(item.quantity) <= 0) {
        showError(new Error(`Invalid quantity for item ${i + 1}`), 'Quantity must be greater than 0');
        return;
      }
      if (!item.price || parseFloat(item.price) <= 0) {
        showError(new Error(`Invalid price for item ${i + 1}`), 'Price must be greater than 0');
        return;
      }
    }

    setSavingChanges(true);
    try {
      const { data: updateResult, error: updateError } = await supabase
        .rpc('update_order_items', {
          order_id_param: viewingOrder.id,
          updated_items: editedItems
        });

      if (updateError) throw updateError;

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update order items');
      }

      // Update the viewing order with the new items
      setViewingOrder(prev => ({
        ...prev,
        cart_items: editedItems
      }));

      setIsEditMode(false);
      showNotification('Order items updated successfully', 'success');
      
      // Refresh the pending orders to show updated totals
      fetchPendingOrders();

    } catch (error) {
      console.error('Error updating order items:', error);
      showError(error, 'Failed to update order items');
    } finally {
      setSavingChanges(false);
    }
  };

  const closeModal = () => {
    setIsViewModalOpen(false);
    setViewingOrder(null);
    setApprovalNotes('');
    setRejectionReason('');
    setActionType('');
    setIsEditMode(false);
    setEditedItems([]);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Order Approvals"
        description="Review and approve customer orders before processing."
        meta={
          <span className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{pendingOrders.length} pending approval{pendingOrders.length !== 1 ? 's' : ''}</span>
          </span>
        }
      />

      {/* Orders Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-4"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : pendingOrders.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
          <p className="mt-1 text-sm text-gray-500">All orders have been processed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pendingOrders.map((order) => {
            const orderTotal = calculateOrderTotal(order.cart_items);
            const isProcessing = approving[order.id];
            
            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        #{order.order_number}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {format(new Date(order.created_at), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                        Pending
                      </span>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                      <User className="w-4 h-4" />
                      <span className="font-medium">
                        {order.accounts?.company_name || order.accounts?.full_name}
                      </span>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Package className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Items:</span>
                      </div>
                      <span className="font-medium">{order.cart_items?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Total:</span>
                      </div>
                      <span className="font-bold text-green-600">
                        ${orderTotal.toFixed(2)}
                      </span>
                    </div>
                    {order.has_backorders && (
                      <div className="flex items-center space-x-2 text-sm text-orange-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Has backorder items</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApproveOrder(order)}
                      disabled={isProcessing}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectOrder(order)}
                      disabled={isProcessing}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleDeleteOrder(order)}
                      disabled={isProcessing}
                      className="px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                      title="Delete Order"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setViewingOrder(order);
                        setActionType('view');
                        setIsViewModalOpen(true);
                      }}
                      className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal */}
      {isViewModalOpen && viewingOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Order #{viewingOrder.order_number} - {actionType === 'view' ? 'Details' : actionType === 'approve' ? 'Approve Order' : 'Reject Order'}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                      <p className="text-sm text-gray-600">
                        <strong>Company:</strong> {viewingOrder.accounts?.company_name || viewingOrder.accounts?.full_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Email:</strong> {viewingOrder.accounts?.email}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Order Details</h4>
                      <p className="text-sm text-gray-600">
                        <strong>Date:</strong> {format(new Date(viewingOrder.created_at), 'PPP')}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Total:</strong> ${calculateOrderTotal(viewingOrder.cart_items).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Delivery Address */}
                  {viewingOrder.delivery_addresses && (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-2">Delivery Address</h4>
                      <p className="text-sm text-gray-600">
                        {viewingOrder.delivery_addresses.address_line1}
                        {viewingOrder.delivery_addresses.address_line2 && <>, {viewingOrder.delivery_addresses.address_line2}</>}
                        <br />
                        {viewingOrder.delivery_addresses.city}, {viewingOrder.delivery_addresses.state} {viewingOrder.delivery_addresses.postal_code}
                      </p>
                    </div>
                  )}

                  {/* Order Instructions */}
                  {viewingOrder.order_instructions && (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-2">Order Instructions</h4>
                      <p className="text-sm text-gray-600">{viewingOrder.order_instructions}</p>
                    </div>
                  )}

                  {/* Items */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">Order Items</h4>
                      {!isEditMode && actionType !== 'reject' && (
                        <button
                          onClick={handleEditMode}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit Prices/Quantities
                        </button>
                      )}
                      {isEditMode && (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSaveChanges}
                            disabled={savingChanges}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {savingChanges ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {(isEditMode ? editedItems : viewingOrder.cart_items)?.map((item, index) => (
                            <tr key={index} className={item.hasBackorder ? 'bg-orange-50' : ''}>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.partNumber}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.description}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-center">
                                {isEditMode ? (
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-center focus:ring-blue-500 focus:border-blue-500"
                                    min="1"
                                  />
                                ) : (
                                  item.quantity
                                )}
                                {item.hasBackorder && (
                                  <span className="text-orange-600 text-xs block">
                                    (Backorder: {item.backorderQty})
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                {isEditMode ? (
                                  <input
                                    type="number"
                                    value={item.price}
                                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                    className="w-24 px-2 py-1 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                                    step="0.01"
                                    min="0"
                                  />
                                ) : (
                                  `$${parseFloat(item.price || 0).toFixed(2)}`
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                                ${(parseFloat(item.price || 0) * parseInt(item.quantity || 0)).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50">
                            <td colSpan="4" className="px-3 py-2 text-sm font-medium text-gray-900 text-right">Total:</td>
                            <td className="px-3 py-2 text-sm font-bold text-gray-900 text-right">
                              ${calculateOrderTotal(isEditMode ? editedItems : viewingOrder.cart_items).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Action-specific forms */}
                  {actionType === 'approve' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Approval Notes (Optional)
                      </label>
                      <textarea
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                        rows="3"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                        placeholder="Add any notes about this approval..."
                      />
                    </div>
                  )}

                  {actionType === 'reject' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows="3"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                        placeholder="Please provide a reason for rejecting this order..."
                        required
                      />
                    </div>
                  )}

                  {actionType === 'delete' && (
                    <div className="mb-4">
                      <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                              Confirm Order Deletion
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>
                                Are you sure you want to permanently delete this order? This action cannot be undone.
                              </p>
                              <div className="mt-2">
                                <p><strong>Order:</strong> #{viewingOrder.order_number}</p>
                                <p><strong>Customer:</strong> {viewingOrder.accounts?.company_name || viewingOrder.accounts?.full_name}</p>
                                <p><strong>Total:</strong> ${calculateOrderTotal(viewingOrder.cart_items).toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {actionType === 'approve' && (
                  <button
                    type="button"
                    onClick={confirmApproval}
                    disabled={approving[viewingOrder.id]}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {approving[viewingOrder.id] ? 'Approving...' : 'Approve Order'}
                  </button>
                )}
                
                {actionType === 'reject' && (
                  <button
                    type="button"
                    onClick={confirmRejection}
                    disabled={approving[viewingOrder.id] || !rejectionReason.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {approving[viewingOrder.id] ? 'Rejecting...' : 'Reject Order'}
                  </button>
                )}

                {actionType === 'delete' && (
                  <button
                    type="button"
                    onClick={confirmDeletion}
                    disabled={approving[viewingOrder.id]}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {approving[viewingOrder.id] ? 'Deleting...' : 'Delete Order'}
                  </button>
                )}

                <button
                  type="button"
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {actionType === 'view' ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderApproval;
